import React, { useState } from 'react';
import { Database, RefreshCw, CheckCircle2 } from 'lucide-react';

export interface ErpSlotOption {
  key: string;
  label: string;
  suite: 'school' | 'app' | 'coach';
  sampleData: string;
}

const SLOT_OPTIONS: ErpSlotOption[] = [
  { key: 'school.notices', label: 'School Notices & Circulars', suite: 'school', sampleData: '{"title":"Annual Day Notice","date":"2026-11-10"}' },
  { key: 'school.admissions_cycles', label: 'Open Admissions Cycles', suite: 'school', sampleData: '{"grade":"Grade 1","status":"Open","seats":14}' },
  { key: 'app.inventory_catalog', label: 'Catalog Products & Inventory', suite: 'app', sampleData: '{"sku":"PROD-01","name":"Core Hub","stock":28}' },
  { key: 'app.helpdesk_kb', label: 'Public Helpdesk Articles', suite: 'app', sampleData: '{"slug":"getting-started","reads":412}' },
  { key: 'coach.lms_courses', label: 'Live LMS Courses & Batches', suite: 'coach', sampleData: '{"course":"JEE Advanced","duration":"12 Months"}' },
  { key: 'coach.toppers_wall', label: 'Toppers Wall of Fame', suite: 'coach', sampleData: '{"student":"Aarav Patel","rank":"AIR 4"}' },
];

export interface ErpBlockConfiguratorProps {
  selectedSlotKey: string;
  onSelectSlot: (slotKey: string) => void;
}

// ErpBlockConfigurator configures live dynamic ERP slot bindings with preview.
export function ErpBlockConfigurator({
  selectedSlotKey,
  onSelectSlot,
}: ErpBlockConfiguratorProps): React.JSX.Element {
  const [suiteFilter, setSuiteFilter] = useState<'all' | 'school' | 'app' | 'coach'>('all');

  const filteredSlots = SLOT_OPTIONS.filter((s) => suiteFilter === 'all' || s.suite === suiteFilter);
  const activeSlot = SLOT_OPTIONS.find((s) => s.key === selectedSlotKey) || SLOT_OPTIONS[0];

  return (
    <div className="p-6 bg-card border border-border rounded-xl flex flex-col gap-6">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Live ERP Dynamic Block Slots</h2>
        </div>
        <div className="flex gap-1.5">
          {(['all', 'school', 'app', 'coach'] as const).map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setSuiteFilter(filter)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold uppercase transition-colors min-h-[44px] ${
                suiteFilter === filter
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          {filteredSlots.map((opt) => {
            const isSelected = opt.key === selectedSlotKey;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onSelectSlot(opt.key)}
                className={`flex items-center justify-between p-3 rounded-lg border text-left transition-colors min-h-[44px] ${
                  isSelected
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                <div>
                  <p className="text-sm font-semibold text-foreground">{opt.label}</p>
                  <p className="text-xs font-mono text-muted-foreground">{opt.key}</p>
                </div>
                {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </button>
            );
          })}
        </div>

        <div className="p-4 rounded-xl border border-border bg-background flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Slot Preview Payload</span>
            <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <pre className="p-3 rounded-lg bg-muted text-foreground text-xs font-mono overflow-x-auto">
            {activeSlot.sampleData}
          </pre>
        </div>
      </div>
    </div>
  );
}
